/**
 * Departmental Memory Recall Card
 *
 * Rendered inline in the Ni² conversation for RECALL_DEPT_MEMORY
 * intents. Uses repository.searchDepartmentMemory to unify workbook
 * seed M&M records with locally published demo M&Ms, and cites
 * every underlying source (case, transcript segments, reflections,
 * pearls, imaging, literature, prior workbook M&Ms) with a clear
 * "This department" vs "Individual reflections" vs "Representative
 * literature" split so a future resident can understand *what kind
 * of memory* each citation represents.
 */
import { useMemo } from "react";
import { Sparkles, Layers } from "lucide-react";
import {
  searchDepartmentMemory,
  getCaseById,
  getLiteratureByIds,
  getPerson,
} from "@/data/repository";
import { useOptionalMMBuilderStore } from "@/context/MMBuilderStore";
import { formatCaseDate } from "@/data/viewModels";

const CANON_STEPS = [
  "Pause dissection the moment facial EMG amplitude drops ≥ 50 percent or the stimulation threshold rises past baseline.",
  "Release all traction on the tumor capsule and any adjacent adherent brainstem interface before doing anything else.",
  "Remap the proximal and distal facial nerve from the root exit zone outward to re-establish continuity.",
  "Reassess the dissection plane; if no anatomical plane is definable, do not force it.",
  "Consider an intentional residual when thresholds continue to worsen or a safe plane cannot be found. Trade a small residual for functional nerve preservation.",
];

export default function MMDepartmentMemoryCard({ payload }) {
  const store = useOptionalMMBuilderStore();
  const publishedMMs = store?.publishedMMs || [];

  const results = useMemo(
    () => searchDepartmentMemory(payload?.query || "facial nerve monitoring", { publishedMMs }),
    [payload?.query, publishedMMs]
  );

  const demoResult = results.find((r) => r.kind === "demo");
  const workbookResult = results.find((r) => r.kind === "workbook");
  const litIds = useMemo(() => {
    const ids = new Set();
    if (demoResult?.record?.synthesis?.literatureIds)
      demoResult.record.synthesis.literatureIds.forEach((id) => ids.add(id));
    if (workbookResult?.record?.literatureIds)
      workbookResult.record.literatureIds.forEach((id) => ids.add(id));
    return [...ids];
  }, [demoResult, workbookResult]);
  const literature = useMemo(() => getLiteratureByIds(litIds).slice(0, 3), [litIds]);

  const primaryCaseId = demoResult?.record?.caseId || workbookResult?.record?.caseId || "VS-RAO-001";
  const caseRec = getCaseById(primaryCaseId);
  const publishedMM = demoResult?.record || null;

  return (
    <div
      className="w-full max-w-[620px] nn-soft-in rounded-2xl border border-white/[0.10] bg-white/[0.04] p-4"
      data-testid="mm-dept-memory-card"
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 12px 32px -16px rgba(0,0,0,0.55)",
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-lg border border-white/[0.10] bg-white/[0.06] text-[hsl(var(--nn-blue))]">
          <Layers className="h-3.5 w-3.5" strokeWidth={1.8} />
        </span>
        <div>
          <div className="nn-eyebrow">Departmental memory</div>
          <div className="text-[13px] font-semibold tracking-tight text-white">
            Facial nerve monitoring decline · vestibular schwannoma
          </div>
        </div>
      </div>

      {/* Canonical steps */}
      <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="nn-eyebrow flex items-center gap-1.5">
          <Sparkles className="h-2.5 w-2.5" strokeWidth={1.8} />
          Departmental protocol
        </div>
        <ol className="mt-1.5 space-y-1 text-[12px] leading-[17px] text-[hsl(var(--nn-ink))]">
          {CANON_STEPS.map((s, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-mono text-[10.5px] text-[hsl(var(--nn-ink-faint))]">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Attribution split — this department vs individual vs literature */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        <AttributionColumn
          label="This department"
          items={[
            publishedMM && { id: publishedMM.mmId, hint: `Published demo M&M · ${formatCaseDate(publishedMM.publishedAt)}`, demo: true },
            workbookResult && { id: workbookResult.record.mAndMId, hint: `Prior workbook M&M · ${formatCaseDate(workbookResult.record.meetingDate)}` },
            caseRec && { id: caseRec.caseId, hint: `Originating case · ${caseRec.diagnosis}` },
          ].filter(Boolean)}
        />
        <AttributionColumn
          label="Individual reflections"
          items={[
            ...(workbookResult?.record?.reflectionIds || []).slice(0, 3).map((id) => ({ id, hint: "Surgeon reflection" })),
            ...((workbookResult?.record || {}).imagingIds || []).slice(0, 1).map((id) => ({ id, hint: "Linked imaging" })),
          ]}
        />
        <AttributionColumn
          label="Representative literature"
          items={literature.map((l) => ({
            id: l.literatureId,
            hint: `${l.publicationYear} · ${l.studyType}`,
          }))}
        />
      </div>

      {/* Attributed faculty comments from the published demo M&M */}
      {publishedMM && (
        <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="nn-eyebrow">Attributed faculty comments · from {publishedMM.mmId}</div>
          <div className="mt-2 flex flex-col gap-1.5">
            {(publishedMM.synthesis?.supportingEvidence?.collaborationIds || []).slice(0, 3).map((id) => (
              <div key={id} className="flex items-center justify-between text-[11px]">
                <span className="text-[hsl(var(--nn-ink))]">Provenance-linked faculty note</span>
                <span className="font-mono text-[10px] text-[hsl(var(--nn-ink-faint))]">{id}</span>
              </div>
            ))}
            {(publishedMM.synthesis?.supportingEvidence?.transcriptSegmentIds || []).slice(0, 2).map((id) => (
              <div key={id} className="flex items-center justify-between text-[11px]">
                <span className="text-[hsl(var(--nn-ink))]">Transcript segment</span>
                <span className="font-mono text-[10px] text-[hsl(var(--nn-ink-faint))]">{id}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 text-[10px] text-[hsl(var(--nn-ink-faint))]">
        Provenance is expandable — click any ID to trace the underlying source.
      </div>
    </div>
  );
}

function AttributionColumn({ label, items = [] }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="text-[9.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        {label}
      </div>
      <div className="mt-1.5 flex flex-col gap-1">
        {items.length === 0 && <span className="text-[10.5px] text-[hsl(var(--nn-ink-faint))]">—</span>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between text-[10.5px]">
            <span className="min-w-0 flex-1 truncate text-[hsl(var(--nn-ink-muted))]">{it.hint}</span>
            <span className={"ml-1 shrink-0 font-mono text-[10px] " + (it.demo ? "text-[hsl(var(--nn-ok))]" : "text-[hsl(var(--nn-ink))]")}>{it.id}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
